/**
 * Draggable Slider
 * A responsive, touch-friendly slider with smooth animations and continuous infinite looping
 * 
 * Usage: Add data-slider attribute to slider container
 * Required structure:
 * - [data-slider] - Main container
 * - [data-slider-track] - Track element that moves
 * - .draggable-slide - Individual slide elements
 * - [data-slider-prev] - Previous button (optional)
 * - [data-slider-next] - Next button (optional)
 */

// DEBUG MODE: Set to true to enable detailed console logging, false for production
const SLIDER_DEBUG_MODE = false;

// Global registry for multi-row slider control
window.sliderRegistry = window.sliderRegistry || {};

/**
 * Initialize a single slider
 * Extracted to a function so it can be called lazily
 */
function initializeSlider(slider) {
  // Check if we've ACTUALLY initialized this (not just the flag)
  if (slider._sliderInitialized) {
    return;
  }
  
  // Mark as initialized with internal flag (not data attribute)
  slider._sliderInitialized = true;
  slider.dataset.sliderInitialized = 'true';
    const track = slider.querySelector('[data-slider-track]');
    const slides = slider.querySelectorAll('.draggable-slide');
    
    // Look for navigation buttons - simplified approach
    let prevBtn = slider.querySelector('[data-slider-prev]');
    let nextBtn = slider.querySelector('[data-slider-next]');
    
    // If not found in slider, look in the parent container
    if (!prevBtn || !nextBtn) {
      const parentContainer = slider.closest('.w-full') || slider.parentElement;
      if (parentContainer) {
        prevBtn = prevBtn || parentContainer.querySelector('[data-slider-prev]');
        nextBtn = nextBtn || parentContainer.querySelector('[data-slider-next]');
      }
    }
    
    if (!track || !slides.length) return;
    
    let currentIndex = 0;
    let isTransitioning = false;
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let isDragging = false;
    let startTime = 0;
    let currentTranslateX = 0; // Track the actual current position in pixels
    let startTranslateX = 0;
    let eventHandled = false; // Prevent double handling of mouse/touch events

    // Generate unique ID for this slider early
    const sliderId = 'slider-' + Math.random().toString(36).substr(2, 9);
    
    // Detect mobile once at initialization
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

    // Autoplay variables
    let autoplayTimer = null;
    let isAutoplayEnabled = false;
    let autoplayDelay = 3000;
    let autoplaySpeed = 1; // pixels per frame for smooth scroll
    let isAutoplayScrolling = false;

    // Debug logging for troubleshooting
    const initTime = performance.now();
    function log(...args) {
      if (SLIDER_DEBUG_MODE) {
        const elapsed = (performance.now() - initTime).toFixed(0);
        console.log(`[Slider +${elapsed}ms]`, ...args);
      }
    }
    
    // Initialize autoplay settings
    function initAutoplay() {
      const autoplayAttr = slider.getAttribute('data-autoplay');
      const delayAttr = slider.getAttribute('data-autoplay-delay');
      const speedAttr = slider.getAttribute('data-autoplay-speed');
      
      // Respect the user's reduced-motion preference — no auto-scroll (WCAG 2.2.2 / 2.3.3).
      const prefersReducedMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      isAutoplayEnabled = autoplayAttr === 'true' && !prefersReducedMotion;
      autoplayDelay = delayAttr ? parseInt(delayAttr) : 3000;
      autoplaySpeed = speedAttr ? parseFloat(speedAttr) : 1.0; // Default to 1.0 pixels per frame (60px/sec)
      
      log('Autoplay settings:', { isAutoplayEnabled, autoplayDelay, autoplaySpeed });
      
      if (isAutoplayEnabled) {
        // PERFORMANCE FIX: Wait for images to load before starting autoplay
        // Check if slider has celebrity cards with lazy loading
        const hasEagerImages = track.querySelectorAll('img[loading="eager"]').length > 0;
        
        if (hasEagerImages) {
          // Wait for first 6 images to load before starting autoplay
          waitForImagesToLoad().then(() => {
            log('✅ Images loaded, starting autoplay');
            startAutoplay();
          });
        } else {
          // No eager images, start immediately
          startAutoplay();
        }
      }
    }
    
    // Wait for critical images to load
    function waitForImagesToLoad() {
      return new Promise((resolve) => {
        const images = Array.from(track.querySelectorAll('img[loading="eager"]')).slice(0, 6);
        log('Waiting for images to load:', images.length, 'eager images found');
        
        if (images.length === 0) {
          log('No eager images found, starting autoplay immediately');
          resolve();
          return;
        }
        
        let loadedCount = 0;
        const totalImages = images.length;
        let resolved = false;
        
        // Fallback: start after 200ms regardless (images load fast with fetchpriority="high")
        const timeoutId = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            log('Timeout reached, starting autoplay anyway');
            resolve();
          }
        }, 200);
        
        const checkAllLoaded = () => {
          loadedCount++;
          log(`Image ${loadedCount}/${totalImages} loaded`);
          if (loadedCount >= totalImages && !resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            log('All images loaded, resolving');
            resolve();
          }
        };
        
        images.forEach((img, index) => {
          if (img.complete) {
            log(`Image ${index} already complete`);
            checkAllLoaded();
          } else {
            log(`Image ${index} waiting for load event`);
            img.addEventListener('load', checkAllLoaded, { once: true });
            img.addEventListener('error', checkAllLoaded, { once: true });
          }
        });
      });
    }
    
    // Start autoplay timer
    function startAutoplay() {
      if (!isAutoplayEnabled) return;
      
      stopAutoplay(); // Clear any existing timer
      
      // Use requestAnimationFrame for smooth continuous scrolling
      let frameCount = 0;
      function smoothAutoplay() {
        frameCount++;
        
        // Log first 3 frames and every 60 frames after
        if (frameCount <= 3 || frameCount % 60 === 0) {
          log(`📽️ Frame ${frameCount}: translateX=${currentTranslateX.toFixed(1)}px, dragging=${isDragging}, transitioning=${isTransitioning}`);
        }
        
        if (!isAutoplayEnabled || isDragging || isTransitioning) {
          if (isAutoplayEnabled && !isDragging && !isTransitioning) {
            // Continue the animation loop
            autoplayTimer = requestAnimationFrame(smoothAutoplay);
          }
          return;
        }
        
        isAutoplayScrolling = true;
        
        // Move the slider by autoplaySpeed pixels
        currentTranslateX -= autoplaySpeed;
        
        // Apply the transform (use translate3d for GPU acceleration)
        track.style.transition = 'none';
        track.style.transform = `translate3d(${currentTranslateX}px, 0, 0)`;
        
        // Check if we need to teleport for infinite loop during autoplay
        if (hasInfiniteLoop && cloneCount > 0 && originalSlidesCount > 0) {
          const slideWidth = getSlideWidth();
          if (slideWidth > 0) {
            const currentVisualIndex = -currentTranslateX / slideWidth;
            
            // Teleport when entering clone zones (same logic as checkForLoop)
            if (currentVisualIndex >= cloneCount + originalSlidesCount) {
              const positionInEndClones = currentVisualIndex - (cloneCount + originalSlidesCount);
              const newIndex = cloneCount + positionInEndClones;
              currentTranslateX = -(newIndex * slideWidth);
              track.style.transform = `translate3d(${currentTranslateX}px, 0, 0)`;
            }
          }
        } else {
          // For non-infinite sliders, reverse direction at the end
          const slideWidth = getSlideWidth();
          if (slideWidth > 0) {
            const maxTranslateX = -((originalSlidesCount - 1) * slideWidth);
            if (currentTranslateX <= maxTranslateX) {
              // Reverse direction or jump back to start
              currentTranslateX = 0;
              track.style.transform = `translate3d(0, 0, 0)`;
              log('Non-infinite slider reset to start');
            }
          }
        }
        
        // Continue the animation
        autoplayTimer = requestAnimationFrame(smoothAutoplay);
      }
      
      // Start the smooth scrolling
      log('🚀 STARTING autoplay animation loop NOW with speed:', autoplaySpeed);
      autoplayTimer = requestAnimationFrame(smoothAutoplay);
      
      log('✅ Smooth autoplay started with speed:', autoplaySpeed);
    }
    
    // Stop autoplay timer
    function stopAutoplay() {
      if (autoplayTimer) {
        cancelAnimationFrame(autoplayTimer);
        autoplayTimer = null;
        isAutoplayScrolling = false;
        log('Autoplay stopped');
      }
    }
    
    // Pause autoplay temporarily (for user interactions)
    function pauseAutoplay() {
      if (!isAutoplayEnabled) return;
      
      stopAutoplay();
      
      // Resume autoplay after a delay
      setTimeout(() => {
        if (isAutoplayEnabled && !isDragging && !isTransitioning) {
          startAutoplay();
        }
      }, autoplayDelay);
      
      log('Autoplay paused, will resume after delay');
    }
    
    // Get slides per view based on screen size
    function getSlidesPerView() {
      const width = window.innerWidth;
      
      // Check if this is a draggable-slider (has clone-slide class) vs product-slider
      const cloneSlides = slider.querySelectorAll('.clone-slide');
      
      if (cloneSlides.length > 0) {
        // draggable-slider.liquid responsive breakpoints:
        // Mobile: w-[40%] = 2.5 slides visible
        // Medium: 2.5 slides visible (with peek)
        // Large screens: lg:w-[20%] = 5 slides visible  
        // Extra large: xl:w-[15%] = ~6.7 slides visible
        if (width >= 1280) return 6.7; // xl: w-[15%]
        if (width >= 1024) return 5;   // lg: w-[20%]
        if (width >= 768) return 2.5;  // md: 2.5 slides with peek
        return 2.5; // mobile: w-[40%]
      } else {
        // product-slider.liquid breakpoints: w-1/2 sm:w-1/3 lg:w-1/4 xl:w-1/4
        if (width >= 1024) return 4; // lg and xl: w-1/4 = 4 slides
        if (width >= 640) return 3;  // sm: w-1/3 = 3 slides
        return 2; // mobile: w-1/2 = 2 slides
      }
    }
    
    let slidesPerView = getSlidesPerView();
    
    // Detect clone structure and calculate original slides count
    let originalSlidesCount;
    let cloneCount = 0;
    let hasInfiniteLoop = false;
    let startIndex = 0;
    
    // First check for explicit data attributes (subcollection-slider pattern)
    const dataCloneCount = parseInt(slider.getAttribute('data-clone-count')) || 0;
    const dataOriginalSlides = parseInt(slider.getAttribute('data-original-slides')) || 0;
    const dataHasInfiniteLoop = slider.getAttribute('data-has-infinite-loop') === 'true';
    
    if (dataHasInfiniteLoop && dataCloneCount > 0 && dataOriginalSlides > 0) {
      // subcollection-slider.liquid: explicit data attributes
      cloneCount = dataCloneCount;
      originalSlidesCount = dataOriginalSlides;
      hasInfiniteLoop = true;
      startIndex = cloneCount;
    }
    // Check if we have clone-slide class (draggable-slider.liquid pattern)
    else if (slider.querySelectorAll('.clone-slide').length > 0) {
      // draggable-slider.liquid: all items repeated 3 times
      originalSlidesCount = slides.length / 3;
      cloneCount = originalSlidesCount;
      hasInfiniteLoop = true;
      startIndex = cloneCount;
    } else {
      // product-slider.liquid: no infinite loop by default
      originalSlidesCount = slides.length;
      cloneCount = 0;
      hasInfiniteLoop = false;
      startIndex = 0;
    }
    
    // Set initial index
    // For infinite loop sliders, start at cloneCount to show the first original slide
    if (hasInfiniteLoop) {
      currentIndex = cloneCount;
    } else {
      currentIndex = startIndex;
    }
    
    log('Initial setup:', { currentIndex, hasInfiniteLoop, originalSlidesCount, cloneCount, isMobile });
    
    // Update slides per view on resize
    window.addEventListener('resize', () => {
      const newSlidesPerView = getSlidesPerView();
      if (newSlidesPerView !== slidesPerView) {
        slidesPerView = newSlidesPerView;
        // Recalculate position based on current translate
        const slideWidth = getSlideWidth();
        if (slideWidth > 0) {
          const newIndex = Math.round(-currentTranslateX / slideWidth);
          
          // Ensure we stay within valid bounds
          if (hasInfiniteLoop) {
            currentIndex = Math.max(0, Math.min(newIndex, slides.length - 1));
          } else {
            currentIndex = Math.max(0, Math.min(newIndex, originalSlidesCount - 1));
          }
          
          log('Resize update:', { newSlidesPerView, newIndex, currentIndex });
        }
        
        updateSlider(false);
      }
    });
    
    // Furthest left a non-looping track may travel: the last slide lands against the
    // right edge, so the track never scrolls into empty space. Measured from the real
    // layout rather than slidesPerView, which is tuned for the other sliders' widths.
    function getMaxScroll() {
      if (!track) return 0;
      return Math.max(0, track.scrollWidth - slider.clientWidth);
    }

    function clampToBounds(position) {
      if (hasInfiniteLoop) return position;
      return Math.max(-getMaxScroll(), Math.min(0, position));
    }

    function getSlideWidth() {
      if (!track || slidesPerView <= 0) return 0;
      const calculatedWidth = track.offsetWidth / slidesPerView;
      
      // Use actual slide width instead of calculated width
      const firstSlide = slides[0];
      if (firstSlide) {
        const actualSlideWidth = firstSlide.offsetWidth;
        
        // CRITICAL: Account for gap spacing in flex layouts
        // Get the gap value from computed styles
        const trackStyles = window.getComputedStyle(track);
        const gap = parseFloat(trackStyles.gap) || parseFloat(trackStyles.columnGap) || 0;
        
        // For infinite sliders, we need to include the gap in our width calculation
        // because each slide effectively takes up (slideWidth + gap) of space
        const effectiveWidth = actualSlideWidth + gap;
        
        return effectiveWidth;
      }
      
      log('Fallback to calculated width:', { calculatedWidth });
      return calculatedWidth;
    }
    
    function updateSlider(animate = true, customDuration = 300) {
      // For multi-row navigation, allow transitions to be interrupted
      if (isTransitioning && animate) {
        log('Interrupting existing transition for multi-row navigation');
        // Don't return - allow the transition to be interrupted
      }
      if (!track) return;
      
      const slideWidth = getSlideWidth();
      if (slideWidth <= 0) {
        log('Invalid slide width:', slideWidth);
        return;
      }
      
      log('🔄 updateSlider BEFORE bounds check:', { currentIndex, hasInfiniteLoop, animate });
      
      // For infinite loop, allow going beyond normal bounds but prevent extreme values
      if (hasInfiniteLoop) {
        // Allow some buffer beyond the normal bounds to reach clone areas
        const maxAllowedIndex = slides.length + 5; // Allow going a bit beyond total slides
        const minAllowedIndex = -5; // Allow going a bit before first slide
        if (currentIndex > maxAllowedIndex) currentIndex = maxAllowedIndex;
        if (currentIndex < minAllowedIndex) currentIndex = minAllowedIndex;
        log('🔄 updateSlider infinite bounds check:', { currentIndex, maxAllowedIndex, minAllowedIndex });
      } else {
        // Only clamp for safety in non-infinite mode
        if (currentIndex < 0) currentIndex = 0;
        if (currentIndex >= originalSlidesCount) currentIndex = originalSlidesCount - 1;
        log('📏 updateSlider clamped for non-infinite:', currentIndex);
      }
      
      // Use the current translate position directly (set by navigation methods)
      const translateX = currentTranslateX;
      
      log('🎯 updateSlider:', { currentIndex, slideWidth, translateX, animate });
      
        // Always apply the transform (use translate3d for GPU acceleration)
        track.style.transform = `translate3d(${translateX}px, 0, 0)`;

        if (animate) {
          // Always set up transition for multi-row navigation
          isTransitioning = true;
          track.style.transition = `transform ${customDuration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;

          // Ensure cursor is set to grab during transitions (not grabbing)
          if (!isDragging) {
            slider.style.cursor = 'grab';
          }

          // Clear any existing timeout and drag animation frames
          if (window.sliderTimeout) {
            clearTimeout(window.sliderTimeout);
          }
          if (window.dragAnimationFrame) {
            cancelAnimationFrame(window.dragAnimationFrame);
            window.dragAnimationFrame = null;
          }

          window.sliderTimeout = setTimeout(() => {
            isTransitioning = false;
            // Only restore cursor if not currently dragging
            if (!isDragging) {
              slider.style.cursor = 'grab';
            }
            checkForLoop();
          }, customDuration + 50);
        } else {
          track.style.transition = 'none';
          isTransitioning = false;
          // Ensure cursor is restored when not animating
          if (!isDragging) {
            slider.style.cursor = 'grab';
          }
        }
    }
    
    function checkForLoop() {
      if (!hasInfiniteLoop) return;
      
      const slideWidth = getSlideWidth();
      if (slideWidth <= 0) return;
      
      const currentVisualIndex = -currentTranslateX / slideWidth;
      
      // FIXED INFINITE LOOP: Use teleporting instead of endless cloning!
      // Structure: [3 clones] [50 originals] [3 clones] - NEVER grow beyond this!
      if (cloneCount > 0 && originalSlidesCount > 0) {
        // When we enter the clone zones DEEPLY (not just barely), TELEPORT back
        // Use 0.5 buffer so we only teleport when we're halfway into a clone slide
        const teleportBuffer = 0.5;
        
        // If scrolling RIGHT into end clones (deeply)
        if (currentVisualIndex >= cloneCount + originalSlidesCount + teleportBuffer) {
          // Calculate how far into the end clones we are (with sub-pixel precision!)
          const positionInEndClones = currentVisualIndex - (cloneCount + originalSlidesCount);
          // Teleport to the equivalent position at the start of originals
          // PRESERVE fractional position to avoid snapping!
          const newIndexWithFraction = cloneCount + positionInEndClones;
          currentTranslateX = -(newIndexWithFraction * slideWidth);
          track.style.transition = 'none';
          track.style.transform = `translate3d(${currentTranslateX}px, 0, 0)`;
          log('🔄 Teleported from end clones to originals (FREE-FLOW):', { 
            oldIndex: currentVisualIndex, 
            newIndexWithFraction,
            currentTranslateX 
          });
          return;
        }
        
        // If scrolling LEFT into start clones (deeply)
        if (currentVisualIndex < cloneCount - teleportBuffer) {
          // Calculate how far into the start clones we are (with sub-pixel precision!)
          const positionInStartClones = cloneCount - currentVisualIndex;
          // Teleport to the equivalent position at the end of originals
          // PRESERVE fractional position to avoid snapping!
          const newIndexWithFraction = cloneCount + originalSlidesCount - positionInStartClones;
          currentTranslateX = -(newIndexWithFraction * slideWidth);
          track.style.transition = 'none';
          track.style.transform = `translate3d(${currentTranslateX}px, 0, 0)`;
          log('🔄 Teleported from start clones to originals (FREE-FLOW):', { 
            oldIndex: currentVisualIndex, 
            newIndexWithFraction,
            currentTranslateX 
          });
          return;
        }
      }
      
      // For product-slider.liquid clone pattern (3 clones at each end)
      else if (slides.length > originalSlidesCount) {
        const endCloneStart = cloneCount + originalSlidesCount;
        
        // If we're in the end clones area, jump to beginning
        if (currentVisualIndex >= endCloneStart) {
          const jumpToX = -(cloneCount * slideWidth);
          currentTranslateX = jumpToX;
          currentIndex = cloneCount;
          track.style.transition = 'none';
          track.style.transform = `translate3d(${jumpToX}px, 0, 0)`;
          log('🔄 Product slider jumped to beginning:', { jumpToX, newIndex: currentIndex });
          return;
        }
        
        // If we're in the beginning clones area, jump to end
        if (currentVisualIndex < cloneCount) {
          const jumpToX = -((endCloneStart - 1) * slideWidth);
          currentTranslateX = jumpToX;
          currentIndex = endCloneStart - 1;
          track.style.transition = 'none';
          track.style.transform = `translate3d(${jumpToX}px, 0, 0)`;
          log('🔄 Product slider jumped to end:', { jumpToX, newIndex: currentIndex });
          return;
        }
      }
    }
    
    function nextSlide() {
      if (slides.length === 0) return;

      log('➡️ nextSlide - before:', { currentIndex, currentTranslateX, hasInfiniteLoop });

      const slideWidth = getSlideWidth();
      if (slideWidth <= 0) return;

      // Move by one slide width to the right (negative direction)
      const newTranslateX = currentTranslateX - slideWidth;
      
      if (hasInfiniteLoop) {
        // For infinite loop, just move by one slide width
        currentTranslateX = newTranslateX;
        currentIndex = Math.round(-currentTranslateX / slideWidth);
        log('➡️ nextSlide - infinite loop:', { currentTranslateX, currentIndex });
      } else {
        // For non-infinite, check bounds
        const maxTranslateX = -(originalSlidesCount - 1) * slideWidth;
        const minTranslateX = 0;
        
        if (newTranslateX >= maxTranslateX) {
          currentTranslateX = newTranslateX;
          currentIndex = Math.round(-currentTranslateX / slideWidth);
          log('➡️ nextSlide - within bounds:', { currentTranslateX, currentIndex });
        } else {
          // Wrap to beginning
          currentTranslateX = 0;
          currentIndex = 0;
          log('➡️ nextSlide - wrapped to beginning:', { currentTranslateX, currentIndex });
        }
      }

      // Use faster transition for multi-row navigation
      const isMultiRow = slider.closest('[id^="shopify-block-"]')?.querySelectorAll('[data-slider]').length > 1;
      updateSlider(true, isMultiRow ? 150 : 300);
    }

    function prevSlide() {
      if (slides.length === 0) return;

      log('⬅️ prevSlide - before:', { currentIndex, currentTranslateX, hasInfiniteLoop });

      const slideWidth = getSlideWidth();
      if (slideWidth <= 0) return;

      // Move by one slide width to the left (positive direction)
      const newTranslateX = currentTranslateX + slideWidth;
      
      if (hasInfiniteLoop) {
        // For infinite loop, just move by one slide width
        currentTranslateX = newTranslateX;
        currentIndex = Math.round(-currentTranslateX / slideWidth);
        log('⬅️ prevSlide - infinite loop:', { currentTranslateX, currentIndex });
      } else {
        // For non-infinite, check bounds
        const maxTranslateX = -(originalSlidesCount - 1) * slideWidth;
        const minTranslateX = 0;
        
        if (newTranslateX <= minTranslateX) {
          currentTranslateX = newTranslateX;
          currentIndex = Math.round(-currentTranslateX / slideWidth);
          log('⬅️ prevSlide - within bounds:', { currentTranslateX, currentIndex });
        } else {
          // Wrap to end
          currentTranslateX = maxTranslateX;
          currentIndex = originalSlidesCount - 1;
          log('⬅️ prevSlide - wrapped to end:', { currentTranslateX, currentIndex });
        }
      }

      // Use faster transition for multi-row navigation
      const isMultiRow = slider.closest('[id^="shopify-block-"]')?.querySelectorAll('[data-slider]').length > 1;
      updateSlider(true, isMultiRow ? 150 : 300);
    }
    
    // Set the slider ID attribute
    slider.setAttribute('data-slider-id', sliderId);


    window.sliderRegistry[sliderId] = {
      prevSlide: prevSlide,
      nextSlide: nextSlide,
      isTransitioning: () => isTransitioning,
      isDragging: () => isDragging,
      pauseAutoplay: pauseAutoplay
    };

    // Navigation button events - use global registry to control all sliders
    if (prevBtn && !prevBtn.hasAttribute('data-multi-slider-nav')) {
      prevBtn.setAttribute('data-multi-slider-nav', 'true');

      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();

        // Find all sliders within the same Shopify block container
        const shopifyBlock = prevBtn.closest('[id^="shopify-block-"]') || prevBtn.closest('.shopify-block');
        
        if (!shopifyBlock) {
          // Fallback: control the closest slider
          const closestSlider = prevBtn.closest('[data-slider]') || 
                               prevBtn.parentElement?.querySelector('[data-slider]') ||
                               document.querySelector('[data-slider]');
          if (closestSlider) {
            const sliderId = closestSlider.getAttribute('data-slider-id');
            const sliderControl = window.sliderRegistry[sliderId];
            if (sliderControl && !sliderControl.isDragging()) {
              sliderControl.pauseAutoplay();
              sliderControl.prevSlide();
            }
          }
          return;
        }

        const allSliders = shopifyBlock.querySelectorAll('[data-slider]');

        allSliders.forEach((targetSlider) => {
          const sliderId = targetSlider.getAttribute('data-slider-id');
          const sliderControl = window.sliderRegistry[sliderId];

          if (!sliderControl) return;

          if (!sliderControl.isDragging()) {
            sliderControl.pauseAutoplay();
            sliderControl.prevSlide();

            // Ensure cursor is restored after navigation
            setTimeout(() => {
              if (!sliderControl.isDragging()) {
                targetSlider.style.cursor = 'grab';
              }
            }, 100);
          }
        });
      });
    }

    if (nextBtn && !nextBtn.hasAttribute('data-multi-slider-nav')) {
      nextBtn.setAttribute('data-multi-slider-nav', 'true');

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();

        // Find all sliders within the same Shopify block container
        const shopifyBlock = nextBtn.closest('[id^="shopify-block-"]') || nextBtn.closest('.shopify-block');
        
        if (!shopifyBlock) {
          // Fallback: control the closest slider
          const closestSlider = nextBtn.closest('[data-slider]') || 
                               nextBtn.parentElement?.querySelector('[data-slider]') ||
                               document.querySelector('[data-slider]');
          if (closestSlider) {
            const sliderId = closestSlider.getAttribute('data-slider-id');
            const sliderControl = window.sliderRegistry[sliderId];
            if (sliderControl && !sliderControl.isDragging()) {
              sliderControl.pauseAutoplay();
              sliderControl.nextSlide();
            }
          }
          return;
        }

        const allSliders = shopifyBlock.querySelectorAll('[data-slider]');

        allSliders.forEach((targetSlider) => {
          const sliderId = targetSlider.getAttribute('data-slider-id');
          const sliderControl = window.sliderRegistry[sliderId];

          if (!sliderControl) return;

          if (!sliderControl.isDragging()) {
            sliderControl.pauseAutoplay();
            sliderControl.nextSlide();

            // Ensure cursor is restored after navigation
            setTimeout(() => {
              if (!sliderControl.isDragging()) {
                targetSlider.style.cursor = 'grab';
              }
            }, 100);
          }
        });
      });
    }
    
    // Touch/Mouse events
    function getEventX(e) {
      return e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    }
    
    function getEventY(e) {
      return e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    }
    
    function handleStart(e) {
      // Allow drag to start even during transitions for better UX
      if (isDragging && eventHandled) return;
      
      // Don't set isDragging = true immediately - wait for gesture detection
      eventHandled = true;
      startX = getEventX(e);
      startY = getEventY(e);
      currentX = startX;
      currentY = startY;
      startTime = Date.now();
      
      log('🎬 handleStart:', { 
        isMobile, 
        startX, 
        startY,
        trackTransition: track.style.transition,
        trackTransform: track.style.transform,
        currentTranslateX: currentTranslateX
      });
      
      // Store the current translate position when we start dragging
      startTranslateX = currentTranslateX;
      
      log('📍 Stored startTranslateX:', startTranslateX);
      
      // Pause autoplay when user starts dragging
      pauseAutoplay();
      
      // Update currentIndex based on current position for proper state management
      if (hasInfiniteLoop) {
        const slideWidth = getSlideWidth();
        if (slideWidth > 0) {
          currentIndex = Math.round(-currentTranslateX / slideWidth);
        }
      }
      
      // CRITICAL: Force remove transitions immediately for smooth dragging
      track.style.transition = 'none';
      track.style.webkitTransition = 'none';
      track.style.MozTransition = 'none';
      track.style.OTransition = 'none';
      
      // CRITICAL: Force browser to apply transition removal IMMEDIATELY
      // Without this, the previous transition is still active and causes jank!
      void track.offsetHeight; // Force reflow - ensures transition removal is processed NOW
      
      if (isMobile) {
        track.style.willChange = 'transform';
      }
      
      log('🎬 After transition removal + forced reflow:', {
        transition: track.style.transition,
        webkitTransition: track.style.webkitTransition,
        willChange: track.style.willChange,
        computedTransition: window.getComputedStyle(track).transition,
        computedTransform: window.getComputedStyle(track).transform
      });
      
      slider.style.cursor = 'grabbing';
      
      // Don't prevent default on touch start - let the browser decide based on movement
      if (e.type.includes('mouse')) {
        e.preventDefault();
      }
    }
    
    function handleMove(e) {
      // If we haven't started tracking movement yet, check if we should
      if (!isDragging && !eventHandled) return;
      
      currentX = getEventX(e);
      currentY = getEventY(e);
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      
      // Determine if this is a horizontal or vertical gesture
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      
      // Mobile: Lower threshold for more responsive detection (5px vs 10px)
      // Desktop: Keep 10px threshold
      const threshold = isMobile ? 5 : 10;
      
      // If we haven't moved much, don't do anything yet
      if (absX < threshold && absY < threshold) {
        return;
      }
      
      // If this is primarily a vertical gesture, allow default scrolling behavior
      // Mobile: Be more lenient - only treat as vertical if MUCH more vertical than horizontal
      const verticalBias = isMobile ? 1.5 : 1;
      if (absY > absX * verticalBias && absY > threshold) {
        // This is a vertical scroll - reset and allow page scroll
        isDragging = false;
        eventHandled = false;
        slider.style.cursor = 'grab';
        return;
      }
      
      // If this is primarily a horizontal gesture, start dragging and prevent default
      // Mobile: Start dragging more eagerly
      if (absX >= absY / verticalBias && absX >= threshold) {
        if (!isDragging) {
          isDragging = true;
          slider.style.cursor = 'grabbing';
          
          // CRITICAL: Reset to CURRENT VISUAL position when drag starts
          // Read the actual computed transform to get where the slider IS NOW
          const computedTransform = window.getComputedStyle(track).transform;
          if (computedTransform && computedTransform !== 'none') {
            // Parse the matrix to get the current X translation
            const matrix = computedTransform.match(/matrix\(([^)]+)\)/);
            if (matrix) {
              const values = matrix[1].split(',').map(parseFloat);
              // For 2D transforms: matrix(a, b, c, d, tx, ty) - tx is at index 4
              currentTranslateX = values[4] || currentTranslateX;
            }
          }
          
          // Now reset baselines to current position
          startX = currentX;
          startY = currentY;
          startTranslateX = currentTranslateX;
          
          log('🎯 Started horizontal drag - RESET baseline:', { 
            absX, 
            absY, 
            threshold,
            newStartX: startX,
            newStartTranslateX: startTranslateX,
            computedTransform
          });
        }
        
        // Prevent default to stop scrolling when we're dragging horizontally
        // Only do this once we've confirmed it's a horizontal gesture
        if (isDragging) {
          e.preventDefault();
        }
        
        // Calculate position from where we started dragging (now with reset baseline)
        const adjustedDeltaX = currentX - startX;
        const adjustedDeltaY = currentY - startY;
        currentTranslateX = startTranslateX + adjustedDeltaX;
        
        log('🔢 Position calc:', { 
          startTranslateX, 
          adjustedDeltaX, 
          result: currentTranslateX,
          formula: `${startTranslateX} + ${adjustedDeltaX} = ${currentTranslateX}`
        });
        
        // Mobile: Apply transform directly for instant response (no RAF delay)
        // Desktop: Use RAF for smoother animation
        if (isMobile) {
          // Direct update on mobile - no frame delay, no reflow checks
          // Transition is already removed in handleStart, no need to check every frame
          track.style.transform = `translate3d(${currentTranslateX}px, 0, 0)`;
          
          log('🎨 handleMove applying transform:', {
            adjustedDeltaX: Math.round(adjustedDeltaX),
            currentTranslateX: Math.round(currentTranslateX)
          });
        } else {
          // Use requestAnimationFrame for smoother dragging on desktop
          if (!window.dragAnimationFrame) {
            window.dragAnimationFrame = requestAnimationFrame(() => {
              // Apply the transform immediately with no transition
              track.style.transform = `translate3d(${currentTranslateX}px, 0, 0)`;
              window.dragAnimationFrame = null;
            });
          }
        }
      }
    }
    
    function handleEnd(e) {
      if (!isDragging && !eventHandled) return;
      
      isDragging = false;
      eventHandled = false;
      slider.style.cursor = 'grab';
      
      // Cancel any pending animation frame
      if (window.dragAnimationFrame) {
        cancelAnimationFrame(window.dragAnimationFrame);
        window.dragAnimationFrame = null;
      }
      
      const deltaX = currentX - startX;
      const velocity = Math.abs(deltaX) / (Date.now() - startTime);
      
      // For infinite loop, still need to update currentIndex for loop detection
      if (hasInfiniteLoop) {
        const slideWidth = getSlideWidth();
        if (slideWidth > 0) {
          // Calculate approximate index for infinite loop detection (but don't snap to it)
          const approximateIndex = (-currentTranslateX / slideWidth) + cloneCount;
          currentIndex = Math.max(0, Math.min(Math.round(approximateIndex), slides.length - 1));
        }
      }
      
      // If user dragged significantly, prevent any link clicks
      const wasDragging = Math.abs(deltaX) > 10 || velocity > 0.1;
      
      if (wasDragging) {
        // Add a temporary class to prevent link clicks
        slider.classList.add('dragging-prevent-click');
        setTimeout(() => {
          slider.classList.remove('dragging-prevent-click');
        }, 100);
      }
      
      // Mobile: Free-form dragging - NO snapping, just stop where released
      // Desktop: Smooth momentum glide
      let finalPosition = currentTranslateX;
      let transitionDuration = 0;
      
      if (isMobile) {
        // Mobile: Add momentum/bounce effect based on swipe velocity
        if (velocity > 0.2 && Math.abs(deltaX) > 30) {
          // Calculate momentum distance based on velocity and drag distance
          // More velocity = more glide
          const momentumMultiplier = Math.min(velocity * 2, 3); // Cap at 3x
          const momentumDistance = Math.min(Math.abs(deltaX) * momentumMultiplier, 400); // Max 400px glide
          const direction = deltaX < 0 ? -1 : 1;
          finalPosition = clampToBounds(currentTranslateX + (momentumDistance * direction));
          
          // Smooth deceleration curve for natural feel
          transitionDuration = 350;
          const easingFunction = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'; // Smooth deceleration
          track.style.transition = `transform ${transitionDuration}ms ${easingFunction}`;
          track.style.webkitTransition = `transform ${transitionDuration}ms ${easingFunction}`;
          track.style.transform = `translate3d(${finalPosition}px, 0, 0)`;
          
          log('📍 Mobile: Momentum glide:', {
            velocity: velocity.toFixed(2),
            momentumDistance: Math.round(momentumDistance),
            finalPosition: Math.round(finalPosition)
          });
        } else {
          // Slow drag = stop instantly at release
          finalPosition = clampToBounds(currentTranslateX);
          track.style.transition = 'none';
          track.style.webkitTransition = 'none';
          track.style.transform = `translate3d(${finalPosition}px, 0, 0)`;
          
          log('📍 Mobile: Slow drag, instant stop:', {
            position: Math.round(currentTranslateX)
          });
        }
      } else {
        // Desktop: Add momentum-based glide for smooth finish
        if (velocity > 0.3 && Math.abs(deltaX) > 50) {
          const momentumDistance = Math.min(Math.abs(deltaX) * 0.3, 150);
          const direction = deltaX < 0 ? -1 : 1;
          finalPosition = currentTranslateX + (momentumDistance * direction);
        }
        
        // For non-infinite sliders, constrain finalPosition to valid bounds
        finalPosition = clampToBounds(finalPosition);
        
        // Desktop: Smooth transition
        transitionDuration = 400;
        const easingFunction = 'cubic-bezier(0.25, 1, 0.5, 1)';
        track.style.transition = `transform ${transitionDuration}ms ${easingFunction}`;
        track.style.webkitTransition = `transform ${transitionDuration}ms ${easingFunction}`;
        track.style.transform = `translate3d(${finalPosition}px, 0, 0)`;
      }
      
      // Update our tracking variable to the final position
      currentTranslateX = finalPosition;
      
      // Check for loop after any transition completes
      if (transitionDuration > 0) {
        setTimeout(() => {
          if (!isDragging && !isTransitioning) {
            checkForLoop();
          }
        }, transitionDuration + 50);
      } else {
        // Mobile: Check immediately since no transition
        if (!isDragging && !isTransitioning) {
          checkForLoop();
        }
      }
    }
    
    // Mouse events - attach to slider container for better drag coverage
    slider.addEventListener('mousedown', handleStart);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    
    // Touch events - CRITICAL OPTIMIZATION for smooth vertical scrolling
    // SOLUTION: Make touchstart/touchend passive, but touchmove needs to be non-passive
    // ONLY when we detect horizontal dragging to allow preventDefault
    slider.addEventListener('touchstart', handleStart, { passive: true });
    slider.addEventListener('touchmove', handleMove, { passive: false }); // Non-passive for horizontal drag
    slider.addEventListener('touchend', handleEnd, { passive: true });
    slider.addEventListener('touchcancel', handleEnd, { passive: true });
    
    // Prevent text selection while dragging
    slider.addEventListener('selectstart', e => e.preventDefault());
    
    // Pause autoplay on hover (with delay to allow initial autoplay to be visible)
    let hoverPauseEnabled = false;
    setTimeout(() => {
      hoverPauseEnabled = true;
      log('Hover-pause now enabled');
    }, 3000); // Enable hover-pause after 3 seconds
    
    slider.addEventListener('mouseenter', () => {
      if (isAutoplayEnabled && hoverPauseEnabled) {
        stopAutoplay();
        log('Autoplay paused on hover');
      }
    });
    
    slider.addEventListener('mouseleave', () => {
      if (isAutoplayEnabled && !isDragging && !isTransitioning && hoverPauseEnabled) {
        startAutoplay();
        log('Autoplay resumed after hover');
      }
    });
    
    // Prevent link clicks when dragging
    slider.addEventListener('click', function(e) {
      if (slider.classList.contains('dragging-prevent-click')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    
    // Initialize
    slider.style.cursor = 'grab';
    slider.style.userSelect = 'none';
    slider.style.webkitUserSelect = 'none';
    slider.style.mozUserSelect = 'none';
    slider.style.msUserSelect = 'none';
    
    // Mobile: Enable hardware acceleration and prevent touch conflicts
    if (isMobile && track) {
      // GPU acceleration hints
      track.style.willChange = 'transform';
      track.style.webkitTransform = 'translateZ(0)'; // Force GPU acceleration
      track.style.backfaceVisibility = 'hidden';
      track.style.perspective = '1000px';
      
      // Critical: Prevent browser from interfering with horizontal drag
      track.style.touchAction = 'pan-y pinch-zoom'; // Allow vertical scroll, block horizontal
      slider.style.touchAction = 'pan-y pinch-zoom';
      
      // Ensure no lingering transitions
      track.style.transition = 'none';
      
      // Force repaint to ensure styles are applied
      track.offsetHeight;
    }
    
    // Set initial position
    const initialSlideWidth = getSlideWidth();
    if (initialSlideWidth > 0) {
      if (hasInfiniteLoop) {
        // Check if clones already exist in the HTML (created by Liquid template)
        const existingClones = track.querySelectorAll('.clone-slide');
        const clonesExistInHtml = existingClones.length > 0;

        if (clonesExistInHtml) {
          // Clones already exist in HTML - use the data attributes for positioning
          // Structure is: [cloneCount clones] + [originalSlidesCount originals] + [cloneCount clones]
          log('Using existing HTML clones:', { cloneCount, originalSlidesCount, existingClonesCount: existingClones.length });
        } else {
          // Create initial clones dynamically since Liquid template doesn't have them
          const originalSlides = Array.from(track.children).slice(0, originalSlidesCount);

          // Add clones at the beginning
          for (let i = originalSlidesCount - 1; i >= 0; i--) {
            const clone = originalSlides[i].cloneNode(true);
            clone.classList.add('clone-slide', 'initial-start-clone');
            track.insertBefore(clone, track.firstChild);
          }

          // Add clones at the end
          for (let i = 0; i < originalSlidesCount; i++) {
            const clone = originalSlides[i].cloneNode(true);
            clone.classList.add('clone-slide', 'initial-end-clone');
            track.appendChild(clone);
          }

          // Update clone count to reflect the new structure (all originals cloned)
          cloneCount = originalSlidesCount;

          log('Created dynamic clones:', { cloneCount, originalSlidesCount });
        }

        // For infinite loop, start at the first original slide (cloneCount position)
        // This means we translate by -(cloneCount * slideWidth) to show the first original slide
        const initialTranslateX = -(cloneCount * initialSlideWidth);
        currentTranslateX = initialTranslateX;
        track.style.transition = 'none';
        track.style.transform = `translate3d(${initialTranslateX}px, 0, 0)`;

        // Show the slider after positioning is complete by adding initialized class
        // PERFORMANCE: Single RAF is enough since slider is already visible
        requestAnimationFrame(() => {
          track.classList.add('slider-initialized');
        });

        log('Initialized infinite loop - showing first original slide:', {
          currentIndex,
          currentTranslateX,
          cloneCount,
          initialTranslateX,
          slideWidth: initialSlideWidth,
          clonesExistInHtml,
          calculation: `-(${cloneCount} * ${initialSlideWidth}) = ${initialTranslateX}`
        });
      } else {
        currentTranslateX = -(currentIndex * initialSlideWidth);
        updateSlider(false);
        
        // Show the slider after positioning is complete by adding initialized class
        // PERFORMANCE: Single RAF is enough since slider is already visible
        requestAnimationFrame(() => {
          track.classList.add('slider-initialized');
        });
        
        log('Initialized normal slider:', { currentIndex, currentTranslateX });
      }
    }
    
    // Initialize autoplay
    initAutoplay();
}

/**
 * Legacy initialization - auto-init all sliders on DOMContentLoaded
 * Only initializes sliders NOT marked for lazy loading
 */
document.addEventListener('DOMContentLoaded', function() {
  // Check if lazy loading is enabled (check the flag, not the instance)
  if (window.lazySliderEnabled) {
    // Don't initialize anything - let lazy-slider-init handle it
    return;
  } else {
    // Fallback: Initialize all sliders immediately (old behavior)
    const sliders = document.querySelectorAll('[data-slider]');
    sliders.forEach(slider => {
      initializeSlider(slider);
    });
  }
});

/**
 * Listen for lazy initialization events
 */
document.addEventListener('slider:init', function(event) {
  const slider = event.detail.slider;
  if (slider) {
    initializeSlider(slider);
  } else {
    console.error('❌ [DraggableSlider] No slider in event detail!');
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { initializeSlider };
} 